import React, { useEffect, useState, useMemo } from 'react';
import { Input, Select, Button, message } from 'antd';
import { RootState } from '../../store';
import { fetchArtifact, updateArtifact, fetchArtifacts } from '../../store/slices/ArtifactSlice';
import { fetchArtifactTypes } from '../../store/slices/artifactTypeSlice';
import { fetchBusinessModels } from '../../store/slices/BusinessModelSlice';
import { fetchPrimeArtifacts } from '../../store/actions/primeArtifactActions';
import styles from './ArtifactDetails.module.css';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { ArtifactType } from '../../types/artifactTypes';
import { BusinessModel } from '../../types/businessModel';
import { PrimeArtifact } from '../../types/primeArtifact';
import { UpdateArtifact, EnhancedArtifact } from '../../types/artifacts';
import { CompanyArtifactId } from '../../types/artifacts-branded';
import { apiClient } from '../../services/apiClient';
import { useUser } from '../../contexts/UserContext';
import { PrimeArtifactService } from '../../services/primeArtifactService';

const { TextArea } = Input;
const { Option } = Select;

interface ArtifactDetailsProps {
  artifactId: CompanyArtifactId;
  onClose: () => void;
}

export const ArtifactDetails: React.FC<ArtifactDetailsProps> = ({ artifactId, onClose }) => {
  const dispatch = useAppDispatch();
  const { selectedCompany } = useUser();
  const { currentArtifact, currentArtifactLoading, error, artifacts, hierarchy } = useAppSelector(
    (state: RootState) => state.artifacts
  );
  const { items: artifactTypes } = useAppSelector((state: RootState) => state.artifactTypes);
  const { models: businessModels } = useAppSelector((state: RootState) => state.businessModels);
  const { primeArtifacts } = useAppSelector((state: RootState) => state.primeArtifacts);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedTypeId, setEditedTypeId] = useState('');
  const [editedParentId, setEditedParentId] = useState<CompanyArtifactId | null>(null);
  const [editedprime_artifact_id, setEditedprime_artifact_id] = useState<string | null>(null);
  const [editedBusinessModelId, setEditedBusinessModelId] = useState<string | null>(null);
  const [applyingAll, setApplyingAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchedPrimeArtifact, setFetchedPrimeArtifact] = useState<PrimeArtifact | null>(null);
  const [loadingPrimeArtifact, setLoadingPrimeArtifact] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (artifactId) {
        // Only log in development mode and sample to reduce verbosity
        if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
          console.log('[ArtifactDetails] Loading data for artifact ID (sampled):', artifactId);
        }
        try {
          await Promise.all([
            dispatch(fetchArtifact(artifactId)),
            dispatch(fetchArtifactTypes()),
            dispatch(fetchPrimeArtifacts()),
          ]);
        } catch (error) {
          console.error('[ArtifactDetails] Error loading data:', error);
        }
      }
    };
    void loadData();
  }, [dispatch, artifactId]);

  useEffect(() => {
    if (currentArtifact && currentArtifact.artifact_tree_id) {
      void dispatch(fetchBusinessModels(currentArtifact.artifact_tree_id));
      // Fetch artifacts for the current tree to populate parent artifact dropdown
      void dispatch(fetchArtifacts({ 
        treeId: currentArtifact.artifact_tree_id,
        companyId: selectedCompany?.id 
      }));
    }
  }, [dispatch, currentArtifact, selectedCompany?.id]);

  // Fetch specific prime artifact if it's not in the store
  useEffect(() => {
    const fetchPrimeArtifactIfNeeded = async () => {
      if (!currentArtifact?.prime_artifact_id) {
        setFetchedPrimeArtifact(null);
        return;
      }

      // Check if prime artifact is already in the store
      const primeInStore = primeArtifacts.find(
        (artifact) => artifact.id === currentArtifact.prime_artifact_id
      );

      if (primeInStore) {
        setFetchedPrimeArtifact(null); // Use the one from store
        return;
      }

      // Prime artifact not in store, fetch it directly
      try {
        setLoadingPrimeArtifact(true);
        const primeArtifact = await PrimeArtifactService.getById(currentArtifact.prime_artifact_id);
        setFetchedPrimeArtifact(primeArtifact);
        console.log('[ArtifactDetails] Fetched prime artifact directly:', primeArtifact.name);
      } catch (error) {
        console.error('[ArtifactDetails] Error fetching prime artifact:', error);
        setFetchedPrimeArtifact(null);
      } finally {
        setLoadingPrimeArtifact(false);
      }
    };

    void fetchPrimeArtifactIfNeeded();
  }, [currentArtifact?.prime_artifact_id, primeArtifacts]);
  
  // Flatten hierarchy to get all artifacts as a fallback if artifacts array is empty
  const flattenHierarchy = (nodes: EnhancedArtifact[]): EnhancedArtifact[] => {
    const result: EnhancedArtifact[] = [];
    const traverse = (node: EnhancedArtifact) => {
      result.push(node);
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(traverse);
      }
    };
    nodes.forEach(traverse);
    return result;
  };
  
  // Get available artifacts for parent selection - use artifacts from Redux, or flatten hierarchy as fallback
  const availableArtifacts = useMemo(() => {
    if (!currentArtifact) return [];
    
    // If we have artifacts in Redux, use those filtered by tree ID
    if (artifacts.length > 0) {
      return artifacts.filter(
        (artifact) => 
          artifact.artifact_tree_id === currentArtifact.artifact_tree_id &&
          artifact.id !== currentArtifact.id
      );
    }
    
    // Fallback: flatten hierarchy and filter by tree ID
    if (hierarchy.length > 0) {
      const flattened = flattenHierarchy(hierarchy);
      return flattened.filter(
        (artifact) => 
          artifact.artifact_tree_id === currentArtifact.artifact_tree_id &&
          artifact.id !== currentArtifact.id
      );
    }
    
    return [];
  }, [artifacts, hierarchy, currentArtifact]);

  // Combine prime artifacts from store and fetched prime artifact for the select dropdown
  const availablePrimeArtifacts = useMemo(() => {
    const combined = [...primeArtifacts];
    
    // If we have a fetched prime artifact that's not in the store, add it
    if (fetchedPrimeArtifact && !primeArtifacts.find(a => a.id === fetchedPrimeArtifact.id)) {
      combined.push(fetchedPrimeArtifact);
    }
    
    return combined;
  }, [primeArtifacts, fetchedPrimeArtifact]);

  const primeLinkHint = useMemo(() => {
    if (!currentArtifact || currentArtifact.prime_artifact_id) return null;

    const artifactType = currentArtifact.type?.trim();
    const looksLikePtSlug = Boolean(artifactType?.startsWith('pt-'));
    const looksLikePascalIdentity = Boolean(
      artifactType && /^[A-Z][A-Za-z0-9]*$/.test(artifactType)
    );
    const typeSlug =
      typeof (currentArtifact as { artifact_type_slug?: string }).artifact_type_slug === 'string'
        ? (currentArtifact as { artifact_type_slug?: string }).artifact_type_slug?.trim()
        : undefined;

    if (looksLikePtSlug || looksLikePascalIdentity || typeSlug) {
      const label = artifactType || typeSlug || 'this type';
      return `Artifact type ${label} maps to a prime template on the server. Open this artifact again after refresh if the link was just backfilled.`;
    }

    const metadata = currentArtifact.artifact_metadata;
    if (metadata && typeof metadata === 'object') {
      const typeRef =
        (metadata as Record<string, unknown>).intentml_artifact_type_ref ||
        (metadata as Record<string, unknown>).artifact_type_ref;
      if (typeof typeRef === 'string' && typeRef.trim()) {
        return `IntentML type ref ${typeRef.trim()} can be resolved to a prime artifact automatically.`;
      }
      if ((metadata as Record<string, unknown>).kryspace_artifact_instance_id) {
        return 'This artifact is managed by the Kryspace company model. Prime linkage should be inherited from its type ref, not set manually for each row.';
      }
    }

    return null;
  }, [currentArtifact]);

  useEffect(() => {
    if (currentArtifact) {
      setEditedName(currentArtifact.name || '');
      setEditedDescription(currentArtifact.description || '');
      setEditedTypeId(currentArtifact.artifact_type_id || '');
      setEditedParentId((currentArtifact.parent_ref as CompanyArtifactId) || null);
      setEditedprime_artifact_id(currentArtifact.prime_artifact_id || null);
      setEditedBusinessModelId(currentArtifact.business_model_id || null);
    }
  }, [currentArtifact]);

  const handleSave = async () => {
    if (!currentArtifact) return;

    try {
      setSaving(true);
      // Build update data with null values for clearing fields
      // Always include artifact_type_id and prime_artifact_id even if they haven't changed
      const updateData: any = {
        id: currentArtifact.id,
        name: editedName,
        description: editedDescription || null, // Send null to clear description
        artifact_type_id: editedTypeId || null, // Always include, even if null
        parent_ref: editedParentId || null, // Send null to clear parent
        prime_artifact_id: editedprime_artifact_id || null, // Always include, even if null
        business_model_id: editedBusinessModelId || null, // Send null to clear business model
      };

      console.log('[ArtifactDetails] Saving update:', updateData);
      console.log('[ArtifactDetails] artifact_type_id:', updateData.artifact_type_id, '(current:', currentArtifact.artifact_type_id, ')');
      console.log('[ArtifactDetails] prime_artifact_id:', updateData.prime_artifact_id, '(current:', currentArtifact.prime_artifact_id, ')');

      const result = await dispatch(
        updateArtifact({
          id: currentArtifact.id,
          data: updateData,
        })
      ).unwrap();
      
      // Verify the update actually worked
      if (updateData.artifact_type_id && result?.artifact_type_id !== updateData.artifact_type_id) {
        console.error('[ArtifactDetails] WARNING: artifact_type_id was not updated by backend!');
        void message.warning('Artifact type was not updated. This may be a backend issue. Please try again or contact support.');
      }
      if (updateData.prime_artifact_id !== null && updateData.prime_artifact_id !== undefined) {
        const sentId = String(updateData.prime_artifact_id);
        const receivedId = result?.prime_artifact_id ? String(result.prime_artifact_id) : null;
        if (sentId !== receivedId) {
          console.error('[ArtifactDetails] WARNING: prime_artifact_id was not updated by backend!');
          void message.warning('Prime artifact was not updated. This may be a backend issue. Please try again or contact support.');
        }
      }
      
      console.log('[ArtifactDetails] Update successful');
      void message.success('Artifact updated successfully');
    } catch (error: any) {
      console.error('[ArtifactDetails] Failed to update artifact:', error);
      const errorMessage = error.response?.data?.detail || 
                         error.response?.data?.message || 
                         error.message || 
                         'Failed to update artifact';
      void message.error(`Error updating artifact: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyAllFromPrime = async () => {
    if (!currentArtifact?.prime_artifact_id) {
      void message.warning('This artifact is not linked to a prime artifact. Cannot apply from prime.');
      return;
    }

    const confirmed = window.confirm(
      'This will apply ALL components (states, transitions, properties, and processes) from the linked prime artifact to this company artifact.\n\nThis action may modify existing data.\n\nAre you sure you want to continue?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setApplyingAll(true);
      const response = await apiClient.post(
        `/artifacts/${artifactId}/apply-prime-all`,
        null,
        { params: { prime_artifact_id: currentArtifact.prime_artifact_id } }
      );
      
      const statesCount = response.data.states_created || 0;
      const transitionsCount = response.data.transitions_created || 0;
      const propertiesCount = response.data.properties_created || 0;
      const processesCount = response.data.processes_created || 0;
      
      void message.success(
        `Prime components applied successfully! Created: ${statesCount} states, ${transitionsCount} transitions, ${propertiesCount} properties, ${processesCount} processes`
      );
      
      // Refresh artifact data
      await dispatch(fetchArtifact(artifactId));
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to apply prime components';
      void message.error(`Error: ${errorMessage}`);
      console.error('Error applying all prime components:', err);
    } finally {
      setApplyingAll(false);
    }
  };

  if (currentArtifactLoading && !currentArtifact) {
    return (
      <div className={styles.artifactDetails}>
        <div className={styles.detailsHeader}>
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.artifactDetails}>
        <div className={styles.detailsHeader}>
          <h2>Error</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
        <div className={styles.detailsContent}>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      </div>
    );
  }

  if (!currentArtifact) {
    return (
      <div className={styles.artifactDetails}>
        <div className={styles.detailsHeader}>
          <h2>Artifact Not Found</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
        <div className={styles.detailsContent}>
          <p>The requested artifact could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.artifactDetails}>
      <div className={styles.detailsHeader}>
        <h2>{currentArtifact.name}</h2>
        <div className={styles.headerActions}>
          {currentArtifact.prime_artifact_id && (
            <Button
              onClick={handleApplyAllFromPrime}
              disabled={applyingAll}
              title="Apply all components (states, transitions, properties, processes) from linked prime artifact"
              style={{ marginRight: 8 }}
              className={styles.applyAllFromPrimeButton}
            >
              {applyingAll ? 'Applying...' : 'Apply All from Prime'}
            </Button>
          )}
          <Button type="primary" onClick={handleSave} loading={saving} style={{ marginRight: 8 }}>
            Save
          </Button>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
      </div>

      <div className={styles.detailsContent}>
        <div className={styles.editForm}>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <Input
              id="name"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              placeholder="Enter artifact name"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <TextArea
              id="description"
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              placeholder="Enter description"
              rows={4}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="type">Type</label>
            <Select
              id="type"
              value={editedTypeId || undefined}
              onChange={(value) => setEditedTypeId(value || '')}
              placeholder="Select a type"
              style={{ width: '100%' }}
              allowClear
            >
              {artifactTypes.map((type: ArtifactType) => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </div>
          
          <div className="form-group">
            <label htmlFor="primeArtifact">Prime Artifact</label>
            <Select
              id="primeArtifact"
              value={editedprime_artifact_id || undefined}
              onChange={(value) => setEditedprime_artifact_id(value || null)}
              placeholder="Search and select a prime artifact"
              style={{ width: '100%' }}
              showSearch
              allowClear
              loading={loadingPrimeArtifact}
              filterOption={(input, option) => {
                const searchLower = input.toLowerCase();
                const label = option?.label || '';
                const labelStr = typeof label === 'string' ? label : String(label || '');
                
                // Find the artifact to search its description too
                const artifact = availablePrimeArtifacts.find(a => a.id === option?.value);
                const description = artifact?.description?.toLowerCase() || '';
                
                return labelStr.toLowerCase().includes(searchLower) || description.includes(searchLower);
              }}
              optionLabelProp="label"
            >
              <Option value="" label="No prime artifact">
                No prime artifact
              </Option>
              {availablePrimeArtifacts.map((artifact: PrimeArtifact) => (
                <Option 
                  key={artifact.id} 
                  value={artifact.id}
                  label={artifact.name}
                >
                  {artifact.name}
                  {artifact.description && (
                    <span style={{ color: '#999', fontSize: '0.9em', marginLeft: 8 }}>
                      - {artifact.description}
                    </span>
                  )}
                </Option>
              ))}
            </Select>
            {!editedprime_artifact_id && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: '0.85em',
                  color: primeLinkHint ? '#1677ff' : '#ff4d4f',
                }}
              >
                {primeLinkHint ||
                  'Link a prime artifact to inherit states, properties, and processes. Use Apply from Prime after linking.'}
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="parent">Parent Artifact</label>
            <Select
              id="parent"
              value={editedParentId || undefined}
              onChange={(value) => setEditedParentId((value as CompanyArtifactId) || null)}
              placeholder="Select a parent artifact"
              style={{ width: '100%' }}
              allowClear
            >
              {availableArtifacts.map((artifact) => (
                <Option key={artifact.id} value={artifact.id}>
                  {artifact.name}
                </Option>
              ))}
            </Select>
          </div>
          
          <div className="form-group">
            <label htmlFor="businessModel">Business Model</label>
            <Select
              id="businessModel"
              value={editedBusinessModelId || undefined}
              onChange={(value) => setEditedBusinessModelId(value || null)}
              placeholder="Select a business model"
              style={{ width: '100%' }}
              allowClear
            >
              {businessModels.map((model: BusinessModel) => (
                <Option key={model.id} value={model.id}>
                  {model.name}
                </Option>
              ))}
            </Select>
          </div>
          
          {/* Read-only fields */}
          <div className="form-group">
            <label>Created</label>
            <div style={{ padding: '4px 11px', border: '1px solid #d9d9d9', borderRadius: '2px', backgroundColor: '#fafafa', color: '#595959' }}>
              {currentArtifact.created_at ? new Date(currentArtifact.created_at).toLocaleString() : 'Unknown'}
            </div>
          </div>
          
          <div className="form-group">
            <label>Last Updated</label>
            <div style={{ padding: '4px 11px', border: '1px solid #d9d9d9', borderRadius: '2px', backgroundColor: '#fafafa', color: '#595959' }}>
              {currentArtifact.updated_at ? new Date(currentArtifact.updated_at).toLocaleString() : 'Unknown'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtifactDetails;
